// @ts-strict-ignore
import { Box, List, sprinkles, Text } from "@saleor/macaw-ui-next";
import { useState } from "react";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";

import { MenuItem } from "./Item";
import { type SidebarMenuItem } from "./types";
import { isMenuActive } from "./utils";

interface Props {
  menuItem: SidebarMenuItem;
}

export const ItemGroup = ({ menuItem }: Props) => {
  const location = useLocation();

  const hasSubmenuActive = menuItem?.children.some(item => isMenuActive(location.pathname, item));
  const isActive = isMenuActive(location.pathname, menuItem) && !hasSubmenuActive;
  const isExpanded = isActive || hasSubmenuActive;

  // macaw 의 ItemGroup 은 defaultExpanded 를 처음 그릴 때만 읽는다. 상단 메뉴바 등으로
  // 이 그룹 안으로 들어오면 key 를 바꿔 다시 그려 펼친다. 나갈 때는 그대로 둬서
  // 사용자가 직접 펼쳐 둔 그룹을 접지 않는다. (2026-10-01 KST 추가)
  const [expandKey, setExpandKey] = useState(0);
  const [wasExpanded, setWasExpanded] = useState(isExpanded);

  if (isExpanded !== wasExpanded) {
    setWasExpanded(isExpanded);

    if (isExpanded) {
      setExpandKey(key => key + 1);
    }
  }

  const handleMenuGroupClick = () => {
    if (menuItem.onClick) {
      menuItem.onClick();
    }
  };

  return (
    <List.ItemGroup key={expandKey} defaultExpanded={isExpanded} data-test-id={`menu-list-item`}>
      <List.ItemGroup.Trigger
        paddingX={2}
        paddingRight={1}
        borderRadius={3}
        size="small"
        active={isActive}
        justifyContent="space-between"
        data-test-id={`menu-item-label-${menuItem.id}`}
        position="relative"
        onClick={handleMenuGroupClick}
        className="sidebar-item-group-trigger"
      >
        <Link
          replace={isActive}
          to={menuItem?.url ?? ""}
          className={sprinkles({
            width: "100%",
            display: "block",
          })}
        >
          <Box display="flex" alignItems="center" gap={3} paddingY={1.5} borderRadius={3}>
            {menuItem.icon}
            {/* 선택된 그룹도 하위 항목과 같은 연두색으로 (2026-09-17 KST 수정) */}
            <Text size={3} fontWeight="medium" color={isActive ? "accent1" : undefined}>
              {menuItem.label}
            </Text>
            {menuItem.endAdornment && <Box>{menuItem.endAdornment}</Box>}
          </Box>
        </Link>
      </List.ItemGroup.Trigger>
      <List.ItemGroup.Content>
        <Box
          borderLeftWidth={1}
          borderLeftStyle="solid"
          borderColor="default1"
          paddingLeft={4}
          marginLeft={4}
          display="flex"
          flexDirection="column"
          marginBottom={2}
          marginTop={1}
          gap="px"
        >
          {menuItem.children?.map(child => (
            <MenuItem menuItem={child} key={child.id} />
          ))}
        </Box>
      </List.ItemGroup.Content>
    </List.ItemGroup>
  );
};
